import React, { useState, useEffect } from 'react';
import { Address } from '../../dto/Address.dto';
import { AddressService } from '../../service/Address.service';
import { ProfileService } from '../../service/Profile.service';
import { ApiService } from '../../../../shared/service/Apiservice';
import { LocationService, AutocompletePrediction } from '../../../../services/location.api.service';
import axios from 'axios';
import { X } from 'lucide-react';
import './NewAddressModal.mobile.css';
import MapComponent from './MapComponent';

interface NewAddressModalMobileProps {
	isOpen: boolean;
	onClose: () => void;
	onAddAddress?: (newAddress: Address, addressId?: string) => void;
	addressToEdit?: Address | null;
}

const initialFormState = {
	fullName: '',
	addressLabel: 'Home',
	phoneNumber: '',
	searchAddress: '',
	floor: '',
	landmark: '',
	name: '',
	locality: '',
};

const NewAddressModalMobile = ({ isOpen, onClose, onAddAddress, addressToEdit }: NewAddressModalMobileProps) => {
	const [formData, setFormData] = useState(initialFormState);
	const [suggestions, setSuggestions] = useState<AutocompletePrediction[]>([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [isLoadingSuggestions] = useState(false);
	const [reverseGeocodedData, setReverseGeocodedData] = useState<any>(null);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const [addresses, setAddresses] = useState<Address[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isLoadingLocation, setIsLoadingLocation] = useState(false);
	const [mapCoordinates, setMapCoordinates] = useState({ latitude: 28.6139, longitude: 77.2090 }); // Default to New Delhi

	useEffect(() => {
		if (addressToEdit && isOpen) {
			setFormData({
				fullName: addressToEdit.primary_address_line || '',
				addressLabel: addressToEdit.label || 'Home',
				phoneNumber: addressToEdit.phone_number || '',
				searchAddress: addressToEdit.locality || '',
				floor: addressToEdit.secondary_address_line || '',
				landmark: addressToEdit.landmark || '',
				name: addressToEdit.name || '',
				locality: addressToEdit.locality || addressToEdit.primary_address_line || '',
			});
			const lat = (addressToEdit as any).latitude ?? (addressToEdit as any).lat;
			const lng = (addressToEdit as any).longitude ?? (addressToEdit as any).lng;
			if (typeof lat === 'number' && typeof lng === 'number') {
				setMapCoordinates({ latitude: lat, longitude: lng });
			}
			setError(null);
			setSuggestions([]);
			setShowSuggestions(false);
		}

		if (!addressToEdit && isOpen) {
			setFormData(initialFormState);
			setError(null);
			setSuggestions([]);
			setShowSuggestions(false);
		}
	}, [addressToEdit, isOpen]);

	const apiService = React.useMemo(() => new ApiService(axios.create()), []);
	const addressService = React.useMemo(() => new AddressService(apiService), [apiService]);
	const profileService = React.useMemo(() => new ProfileService(apiService), [apiService]);
	const locationService = React.useMemo(() => new LocationService(), []);

	const handleAddressLabelChange = (label: string) => {
		setFormData(prev => ({
			...prev,
			addressLabel: label
		}));
		
		if (label === 'Home' && reverseGeocodedData) {
			setFormData(prev => ({
				...prev,
				addressLabel: label,
				fullName: reverseGeocodedData.formattedAddress.split(',').slice(0, -2).join(','),
				searchAddress: reverseGeocodedData.locationName,
				locality: reverseGeocodedData.locality || reverseGeocodedData.sublocality || reverseGeocodedData.area || reverseGeocodedData.city || ''
			}));
			setSuggestions([]);
			setShowSuggestions(false);
		} else {
			setSuggestions([]);
			setShowSuggestions(false);
		}
	};

	const handleSuggestionSelect = async (prediction: AutocompletePrediction) => {
		setSuggestions([]);
		setShowSuggestions(false);

		try {
			const response = await locationService.getPlaceDetails(prediction.placeId);
			if (response.success && response.data) {
				const data = response.data;
				setFormData(prev => ({
					...prev,
					fullName: data.formattedAddress || prediction.description,
					searchAddress: data.locationName || prediction.mainText || prediction.description,
					locality: data.locality || data.sublocality || data.area || data.city || ''
				}));
			} else {
				setFormData(prev => ({
					...prev,
					fullName: prediction.description,
					searchAddress: prediction.mainText || prediction.description
				}));
			}
		} catch (err) {
			setFormData(prev => ({
				...prev,
				fullName: prediction.description,
				searchAddress: prediction.mainText || prediction.description
			}));
		}
	};

	useEffect(() => {
		const fetchUserProfile = async () => {
			try {
				const response = await profileService.getUserProfile();
				if (response.success && response.user && response.user.phone) {
					setFormData(prev => ({
						...prev,
						phoneNumber: response.user?.phone?.replace(/^\+91/, '') || ''
					}));
				}
			} catch (err) {
			}
		};

		const fetchAddresses = async () => {
			try {
				const response = await addressService.getUserAddresses();
				if (response.success && response.data) {
					setAddresses(response.data);
				}
			} catch (err) {
				setError('Failed to fetch addresses');
			}
		};

		const fetchCurrentLocation = async () => {
			if (navigator.geolocation) {
				setIsLoadingLocation(true);
				try {
					const position = await new Promise<GeolocationPosition>((resolve, reject) => {
						navigator.geolocation.getCurrentPosition(resolve, reject, {
							enableHighAccuracy: true,
							timeout: 10000,
							maximumAge: 300000 // 5 minutes
						});
					});

					const { latitude, longitude } = position.coords;
					setMapCoordinates({ latitude, longitude });
					
					const response = await locationService.reverseGeocode(latitude, longitude);
					if (response.success && response.data) {
						const data = response.data;
						setReverseGeocodedData(data);
						
						setFormData(prev => ({
							...prev,
							fullName: data.formattedAddress.split(',').slice(0, -3).join(','),
							searchAddress: data.locationName,
							locality: data.locality || data.sublocality || data.area || data.city || ''
						}));
					}
				} catch (err) {
				} finally {
					setIsLoadingLocation(false);
				}
			}
		};

		if (isOpen) {
			fetchUserProfile();
			fetchAddresses();
			if (!addressToEdit) {
				fetchCurrentLocation();
			}
		}
	}, [isOpen, profileService, addressService, locationService, addressToEdit]);

	const [isSaving, setIsSaving] = React.useState(false);
	const handleAddAddress = async () => {
		if (isSaving) return;
		setError(null);
		const isEditing = Boolean(addressToEdit?._id);

		if (!isEditing && addresses.length >= 5) {
			setError('Maximum 5 addresses allowed. Please delete an existing address first.');
			return;
		}
		const label = formData.addressLabel as 'Home' | 'Work' | 'Other';
		const existingAddressWithSameLabel = addresses.find(addr => addr.label === label);
		if (!isEditing && existingAddressWithSameLabel && label !== 'Other') {
			const shouldOverwrite = window.confirm(
				`You already have a "${formData.addressLabel}" address. Adding this new address will replace the existing one. Do you want to continue?`
			);
			if (!shouldOverwrite) return;
			try {
				if (existingAddressWithSameLabel._id) {
					await addressService.deleteAddress(existingAddressWithSameLabel._id);
				} else {
					setError('Address ID missing. Cannot delete existing address.');
					return;
				}
			} catch (err) {
				setError('Failed to replace existing address. Please try again.');
				return;
			}
		}

		if (isEditing && existingAddressWithSameLabel && existingAddressWithSameLabel._id !== addressToEdit?._id && label !== 'Other') {
			const shouldOverwrite = window.confirm(
				`You already have a "${formData.addressLabel}" address. Updating this address will replace the existing one. Do you want to continue?`
			);
			if (!shouldOverwrite) return;
			try {
				if (existingAddressWithSameLabel._id) {
					await addressService.deleteAddress(existingAddressWithSameLabel._id);
				} else {
					setError('Address ID missing. Cannot delete existing address.');
					return;
				}
			} catch (err) {
				setError('Failed to replace existing address. Please try again.');
				return;
			}
		}
				const newAddress: Address = {
					label,
					name: formData.name || formData.fullName,
					phone_number: formData.phoneNumber,
					primary_address_line: formData.fullName, // Flat/House/Building
					secondary_address_line: formData.floor, // Floor (optional)
					locality: formData.locality || formData.searchAddress, // Area/Sector/Locality
					landmark: formData.landmark, // Nearby landmark (optional)
					latitude: mapCoordinates.latitude,
					longitude: mapCoordinates.longitude,
				};
		try {
			setIsSaving(true);
			if (onAddAddress) {
				await onAddAddress(newAddress, addressToEdit?._id);
			}
			onClose();
			setFormData(initialFormState);
		} finally {
			setIsSaving(false);
		}
	};

	const handleCancel = () => {
		onClose();
			setFormData(initialFormState);
			setError(null);
			setSuggestions([]);
			setShowSuggestions(false);
	};

	if (!isOpen) return null;

	return (
		<>
			<div className="mobile-modal-overlay" onClick={handleCancel}></div>
			<div className="mobile-modal-container">
				<div className="mobile-modal-header">
					<h2 className="mobile-modal-title">{addressToEdit ? 'Edit address' : 'Enter complete address'}</h2>
					<button className="mobile-close-btn" onClick={handleCancel} disabled={isSaving}>
						<X size={24} color="#333" />
					</button>
				</div>
				<div className="mobile-modal-body">
					{/* Map Section */}
					<div className="mobile-map-section">
						<div className="mobile-map-header">
							<h3 className="mobile-map-title">Confirm map pin location</h3>
						</div>
						<div className="mobile-map-container">
							<MapComponent 
								latitude={mapCoordinates.latitude} 
								longitude={mapCoordinates.longitude}
								locationName={formData.searchAddress || 'Your Location'}
								onLocationChange={(lat, lng) => setMapCoordinates({ latitude: lat, longitude: lng })}
							/>
						</div>
					</div>

					<div className="mobile-address-type-row">
						{['Home', 'Work', 'Other'].map(type => (
							<button
								key={type}
								className={`mobile-address-type-btn${formData.addressLabel === type ? ' selected' : ''}`}
								onClick={() => handleAddressLabelChange(type)}
							>
								{type === 'Home' && <span role="img" aria-label="Home">🏠</span>}
								{type === 'Work' && <span role="img" aria-label="Work">🏢</span>}
								{type === 'Other' && <span role="img" aria-label="Other">🔖</span>}
								<span className="mobile-address-type-label">{type}</span>
							</button>
						))}
					</div>
					<form className="mobile-address-form" onSubmit={e => { e.preventDefault(); handleAddAddress(); }}>
						<div style={{ position: 'relative' }}>
							<input
								type="text"
								name="fullName"
								value={formData.fullName}
								onChange={handleInputChange}
								placeholder="Flat / House no / Building name *"
								className="mobile-form-input"
								required
							/>
							{/* Suggestions Dropdown for Flat/House field */}
							{showSuggestions && suggestions.length > 0 && formData.addressLabel !== 'Home' && (
								<div style={{
									position: 'absolute',
									top: '100%',
									left: 0,
									right: 0,
									backgroundColor: '#fff',
									border: '1px solid #ddd',
									borderRadius: '4px',
									maxHeight: '250px',
									overflowY: 'auto',
									zIndex: 1000,
									boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
								}}>
									{suggestions.map((suggestion) => (
										<div
											key={suggestion.placeId}
											onClick={() => handleSuggestionSelect(suggestion)}
											style={{
												padding: '10px 12px',
												borderBottom: '1px solid #eee',
												cursor: 'pointer',
												transition: 'background-color 0.2s',
												fontSize: '14px'
											}}
											onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
											onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
										>
											<div style={{ fontWeight: '500', color: '#333' }}>
												{suggestion.mainText}
											</div>
											<div style={{ fontSize: '12px', color: '#666' }}>
												{suggestion.secondaryText}
											</div>
										</div>
									))}
								</div>
							)}
						</div>
						<input
							type="text"
							name="floor"
							value={formData.floor || ''}
							onChange={handleInputChange}
							placeholder="Floor (optional)"
							className="mobile-form-input"
						/>
						<div style={{ position: 'relative' }}>
							<input
								type="text"
								name="searchAddress"
								value={formData.searchAddress}
								onChange={(e) => setFormData(prev => ({ ...prev, searchAddress: e.target.value }))}
								placeholder={formData.addressLabel === 'Home' ? (isLoadingLocation ? "Detecting your location..." : "Your location") : "Start typing to search..."}
								className="mobile-form-input"
								required
								disabled={isLoadingLocation || (formData.addressLabel === 'Home' && isLoadingLocation)}
								readOnly={formData.addressLabel === 'Home' && isLoadingLocation}
							/>
							{isLoadingLocation && (
								<div style={{ 
									position: 'absolute', 
									right: '10px', 
									top: '50%', 
									transform: 'translateY(-50%)',
									color: '#666'
								}}>
									📍
								</div>
							)}
							{isLoadingSuggestions && formData.addressLabel !== 'Home' && (
								<div style={{ 
									position: 'absolute', 
									right: '10px', 
									top: '50%', 
									transform: 'translateY(-50%)',
									color: '#666'
								}}>
									⏳
								</div>
							)}
						</div>
						<input
							type="text"
							name="landmark"
							value={formData.landmark || ''}
							onChange={handleInputChange}
							placeholder="Nearby landmark (optional)"
							className="mobile-form-input"
						/>
						<input
							type="text"
							name="name"
							value={formData.name || ''}
							onChange={handleInputChange}
							placeholder="Your name *"
							className="mobile-form-input"
							required
						/>
						<input
							type="tel"
							name="phoneNumber"
							value={formData.phoneNumber}
							onChange={handleInputChange}
							placeholder="Your phone number (optional)"
							className="mobile-form-input"
							maxLength={10}
						/>
						{error && (
							<div className="mobile-error-text">{error}</div>
						)}
						<button type="submit" className="mobile-save-btn" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Address'}</button>
					</form>
				</div>
			</div>
		</>
	);
};

export default NewAddressModalMobile;
